import {
  type MeResponse,
  devLoginAccountsResponseSchema,
  isPermission,
  isRoleCode,
  meResponseSchema,
} from '@thrivehrm/shared';
import { ApiError, api } from './api';
import type { CurrentUser } from './auth';

/**
 * The signed-in session as the SPA sees it (ARCHITECTURE §4 step 5). Loaded once on start-up;
 * sign-in and sign-out reload the page, so the router's auth context never goes stale.
 */

/** `/auth/me` → the UI's user. Unknown role codes (future modules) are not shown as chips. */
export function toCurrentUser(me: MeResponse): CurrentUser {
  return {
    userId: me.userId,
    employeeId: me.employee?.id ?? null,
    name: me.name,
    email: me.email,
    roles: me.roles.filter(isRoleCode),
    permissions: new Set(me.permissions.filter(isPermission)),
    hasReports: me.hasReports,
  };
}

/** Current user, or null when not signed in (401) or the API is unreachable. */
export async function loadSession(): Promise<CurrentUser | null> {
  try {
    const me = await api.get('/auth/me', { schema: meResponseSchema, ignoreUnauthorized: true });
    return toCurrentUser(me);
  } catch (error: unknown) {
    if (error instanceof ApiError) return null;
    throw error;
  }
}

/** Only same-origin paths are followed after sign-in (mirrors the API's `safeNextPath`). */
export function safeNext(next: string | undefined): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return '/';
  return next.startsWith('/login') ? '/' : next;
}

/** Full-page navigation (resets all client state). Replaceable in tests. */
export const browser = {
  go(url: string): void {
    window.location.assign(url);
  },
};

/** FR-AUTH-6: ends the server session, then shows /login — even if the call fails. */
export async function signOut(): Promise<void> {
  try {
    await api.post('/auth/logout', undefined, { ignoreUnauthorized: true });
  } finally {
    browser.go('/login?reason=signed_out');
  }
}

/** FR-AUTH-8: "Dev login as…" accounts; null when dev login is off (the API answers 404). */
export async function loadDevAccounts() {
  try {
    const res = await api.get('/auth/dev/accounts', {
      schema: devLoginAccountsResponseSchema,
      ignoreUnauthorized: true,
    });
    return res.items;
  } catch (error: unknown) {
    if (error instanceof ApiError && error.status === 404) return null;
    throw error;
  }
}

export async function devLogin(userId: string, next: string): Promise<void> {
  await api.post('/auth/dev/login', { userId }, { ignoreUnauthorized: true });
  browser.go(safeNext(next));
}

/** A request answered 401 mid-session: the session expired or was ended elsewhere. */
export function sessionExpired(currentHref: string): void {
  browser.go(`/login?reason=expired&next=${encodeURIComponent(safeNext(currentHref))}`);
}
